/**
 * Typed binding for the /admin/users/* endpoints (PR #5a.3).
 *
 * Mirrors what the Admin Module's Users tab needs:
 *   - list(filter)     → left pane of the master/detail view.
 *   - get(id)          → fetch one user on row click.
 *   - create(body)     → "Invite user" form.
 *   - update(id, body) → edit display name / suspend / re-activate (PATCH).
 *
 * All four require an authenticated session AND one of
 * `admin.users.global` / `admin.users.own_entity`. Wire errors that
 * surface here include `user_email_taken`, `not_found`, `forbidden`,
 * and `unauthorized`.
 */

import {
  UserAuthorityOverrideResponseSchema,
  UserDetailResponseSchema,
  UserListResponseSchema,
  UserResponseSchema,
  type UserAuthorityOverrideRequest,
  type UserAuthorityOverrideResponse,
  type UserCreateRequest,
  type UserDetail,
  type UserListQuery,
  type UserListResponse,
  type UserResponse,
  type UserUpdateRequest,
} from '../schemas/user.js';
import type { RequestOptions, Transport } from './transport.js';

export interface UsersClient {
  list(query?: UserListQuery, options?: RequestOptions): Promise<UserListResponse>;
  /** Returns the full detail including resolved authority limits. */
  get(id: string, options?: RequestOptions): Promise<{ user: UserDetail }>;
  create(body: UserCreateRequest, options?: RequestOptions): Promise<UserResponse>;
  update(id: string, body: UserUpdateRequest, options?: RequestOptions): Promise<UserResponse>;
  setAuthority(
    id: string,
    body: UserAuthorityOverrideRequest,
    options?: RequestOptions,
  ): Promise<UserAuthorityOverrideResponse>;
  clearAuthority(id: string, options?: RequestOptions): Promise<UserAuthorityOverrideResponse>;
}

const buildListPath = (query: UserListQuery | undefined): string => {
  if (!query) return '/admin/users';
  const params = new URLSearchParams();
  if (query.entityId !== undefined) params.set('entityId', query.entityId);
  if (query.status !== undefined) params.set('status', query.status);
  if (query.search !== undefined) params.set('search', query.search);
  const qs = params.toString();
  return qs.length === 0 ? '/admin/users' : `/admin/users?${qs}`;
};

export const createUsersClient = (transport: Transport): UsersClient => ({
  list: (query, options) => transport.get(buildListPath(query), UserListResponseSchema, options),
  get: (id, options) =>
    transport.get(`/admin/users/${encodeURIComponent(id)}`, UserDetailResponseSchema, options),
  create: (body, options) => transport.post('/admin/users', UserResponseSchema, body, options),
  update: (id, body, options) =>
    transport.patch(`/admin/users/${encodeURIComponent(id)}`, UserResponseSchema, body, options),
  setAuthority: (id, body, options) =>
    transport.put(
      `/admin/users/${encodeURIComponent(id)}/authority`,
      UserAuthorityOverrideResponseSchema,
      body,
      options,
    ),
  clearAuthority: (id, options) =>
    transport.delete(
      `/admin/users/${encodeURIComponent(id)}/authority`,
      UserAuthorityOverrideResponseSchema,
      options,
    ),
});
